import React, { useMemo } from 'react';
import type { AssetStyleId } from 'fym-shared/src/music24/assetStyles';
import { TypedMemo } from '../../../../common/types';
import { momentOptions } from '../studioRules';
import { studioStoryStyle } from '../frames/studioStory';
import { StudioDetailsProps } from './props';
import { MomentTiles } from './imageTiles/MomentTiles';
import { ShapeTiles } from './imageTiles/ShapeTiles';
import type { StoryPreviewContext } from './imageTiles/storyPreviewContext';
import { LyricLinePanel } from './lyricPanel/LyricLinePanel';

interface ImageDetailsProps extends StudioDetailsProps {
  /** The song and theme facts of the drawn previews. */
  context: StoryPreviewContext;
  /** The style of the step: the mini Story files and the fit meter follow it. */
  style: AssetStyleId | null;
  /** "Add the lyrics": the Text view of the Lyrics section of the song page. */
  onAddLyrics: () => void;
  /** "Change the date": the release date on the song page. */
  onChangeDate: () => void;
}

/**
 * Step 3 for an image (design studio-details a1, a2, b): the moment tiles of a Story and a feed
 * post, the lyric line panel of a lyric Story, a lyric feed post and a lyric card, the shape tiles
 * of a feed post.
 * Banners, the thumbnail, the cover sizes and the link preview have only the style tiles of
 * `DetailsStep`. An image has no Instructions (owner decision 2026-10-08).
 */
const ImageDetailsComponent: React.FC<ImageDetailsProps> = ({
  themeId,
  details,
  song,
  onChange,
  context,
  style,
  onAddLyrics,
  onChangeDate,
}) => {
  const moments = useMemo(() => momentOptions(themeId, song), [song, themeId]);
  const lyricPanel = (assetTypeId: 'story' | 'feedPost' | 'lyricCard') => (
    <LyricLinePanel
      assetTypeId={assetTypeId}
      lyrics={song.lyrics}
      value={details.lyricLine}
      onChange={(lyricLine) => onChange({ lyricLine })}
      releaseThemeId={context.look.themeId}
      fontOverride={context.look.fontOverride}
      // The lyric card is the lyric Story in a square: its meter measures the picked style too.
      storyStyle={studioStoryStyle(style)}
      onOpenSongLyrics={onAddLyrics}
    />
  );

  if (themeId === 'story' || themeId === 'feedPost') {
    return (
      <>
        <MomentTiles
          assetTypeId={themeId}
          options={moments}
          value={details.moment}
          assetStyle={style}
          shape={themeId === 'feedPost' ? details.shape : undefined}
          context={context}
          onAddLyrics={onAddLyrics}
          onChangeDate={onChangeDate}
          onChange={(moment) =>
            // The lyric card replaces the square lyric post (owner answer 2 of page k): a lyric post is 4:5.
            onChange(themeId === 'feedPost' && moment === 'lyricLine' ? { moment, shape: '4:5' } : { moment })
          }
        />
        {details.moment === 'lyricLine' && lyricPanel(themeId)}
        {themeId === 'feedPost' && details.moment !== 'lyricLine' && (
          <ShapeTiles value={details.shape} onChange={(shape) => onChange({ shape })} />
        )}
      </>
    );
  }

  if (themeId === 'lyricCard') {
    return lyricPanel('lyricCard');
  }

  return null;
};

export const ImageDetails = TypedMemo(ImageDetailsComponent);
